/**
 * FlightPathPopup — Two-mode centered overlay for flight path video playback / editing.
 *
 * - **Player mode**: Mini video player with Thermal/Visual toggle, flight stats, Edit button.
 * - **Edit mode**: Flight info + two URL input fields (thermal & visual) + Save/Delete/Close.
 *
 * Opens in player mode if URLs are set, otherwise opens in edit mode.
 * Styled as a centered overlay (like IndicationPopup), not a Mapbox popup.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { humanEditProvenance, topologyValuesEqual } from '../../domain/topology/applyDerivedTopology';
import mapboxgl from 'mapbox-gl';
import type { FlightPath, IndicationType } from '../../stores/projectStore';
import { INDICATION_TYPES } from '../../stores/projectStore';
import { useProjectStore, useMapStore, useUIStore } from '../../stores';
import { useTaskStore } from '../../stores/taskStore';
import { importDriveVideo } from '../../services/assetApi';
import { resolveSignedUrls, devRelativize } from '../../services/signedAssetUrl';
import { buildTileUrl } from '../../services/tileApi';
import { resolveImageSrc, centroidOfPolygon, retypedStringOcGeometry } from '../../utils';
import { Button } from '../common';
import IndicationPopup from '../editor/IndicationPopup';
import type { IndicationFormData } from '../editor/IndicationPopup';
import { PhotoSection } from '../editor/IndicationPreview';
import { driveThumbnailUrl } from '../../utils/driveImport';
import '../editor/IndicationPreview.css';
import './FlightPathPopup.css';

// ─── Props ────────────────────────────────────────────────────────────────────

interface FlightPathPopupProps {
  flightPathId: string;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Compute stats from coordinates/timestamps if not already stored. */
function computeStats(fp: FlightPath) {
  if (fp.flightStats) return fp.flightStats;

  const coords = fp.coordinates;
  const timestamps = fp.timestamps;
  let durationSec: number | undefined;
  let avgAltitude: number | undefined;
  const frameCount = coords.length;

  if (timestamps && timestamps.length >= 2) {
    const parseTs = (ts: string) => {
      const m = ts.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
      if (!m) return 0;
      return parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3]) + parseInt(m[4]) / 1000;
    };
    const start = parseTs(timestamps[0]);
    const end = parseTs(timestamps[timestamps.length - 1]);
    durationSec = end - start;
  }

  const alts = coords.filter((c) => c[2] != null).map((c) => c[2]!);
  if (alts.length > 0) {
    avgAltitude = alts.reduce((a, b) => a + b, 0) / alts.length;
  }

  return { durationSec, frameCount, avgAltitude };
}

/** Format GPS coordinate pair as string. */
function fmtGps(coord: [number, number, number?] | undefined): string {
  if (!coord) return '';
  return `${coord[1].toFixed(7)}, ${coord[0].toFixed(7)}`;
}

// ─── Frame Tracking Helpers ───────────────────────────────────────────────────

/** Parse "HH:MM:SS,mmm" timestamps into seconds array for binary search. */
function buildTimeIndex(timestamps: string[]): number[] {
  return timestamps.map((ts) => {
    const m = ts.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
    if (!m) return 0;
    return parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3]) + parseInt(m[4]) / 1000;
  });
}

/** Find the frame index closest to the given time in seconds (binary search). */
function findFrameAtTime(timeIndex: number[], timeSec: number): number {
  let lo = 0;
  let hi = timeIndex.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (timeIndex[mid] <= timeSec) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** Format seconds as M:SS for elapsed time display. */
function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Video URL Detection ──────────────────────────────────────────────────────

type VideoType = 'google-drive' | 'youtube' | 'vimeo' | 'direct';

function detectVideoType(url: string): VideoType {
  if (/drive\.google\.com\/file\/d\//.test(url)) return 'google-drive';
  if (/(?:youtube\.com\/watch|youtu\.be\/)/.test(url)) return 'youtube';
  if (/vimeo\.com\/\d+/.test(url)) return 'vimeo';
  return 'direct';
}

/** Convert share/watch URLs to embeddable URLs. Returns null for direct file URLs. */
function getEmbedUrl(url: string): string | null {
  const type = detectVideoType(url);
  switch (type) {
    case 'google-drive': {
      const m = url.match(/\/d\/([^/]+)/);
      return m ? `https://drive.google.com/file/d/${m[1]}/preview` : null;
    }
    case 'youtube': {
      const m = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
      return m ? `https://www.youtube.com/embed/${m[1]}` : null;
    }
    case 'vimeo': {
      const m = url.match(/vimeo\.com\/(\d+)/);
      return m ? `https://player.vimeo.com/video/${m[1]}` : null;
    }
    default:
      return null;
  }
}

/** Convert a local filesystem path to a local-video:// URL for Electron playback.
 *  Cloud-hosted URLs (api.soleadlab.com) get dev-relativized so <video> rides
 *  the Vite proxy in `electron:dev` (cookie is bound to localhost there). */
function toVideoSrc(url: string): string {
  if (/^[A-Za-z]:\\/.test(url)) return 'local-video:///' + url.replace(/\\/g, '/');
  // Server-relative `/api/...` URLs are not local filesystem paths; let them
  // fall through to devRelativize so they use the Vite proxy in dev and normal
  // browser/Electron HTTP resolution in production.
  if (url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/api/')) {
    return 'local-video://' + url;
  }
  return devRelativize(url);
}

// ─── Side Panel Fields ───────────────────────────────────────────────────────

const SIDE_FIELDS = [
  { key: 'station', label: 'Sta' },
  { key: 'inverter', label: 'Inv' },
  { key: 'dccb', label: 'DCCB' },
  { key: 'string', label: 'Str' },
  { key: 'module', label: 'Mod' },
] as const;

// ─── IndicationSideView (Stage 1 compact card) ──────────────────────────────

function IndicationSideView({ indicationId, onEdit, onDelete, onClose }: {
  indicationId: string;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  const indication = useProjectStore((s) =>
    s.data.indications.find((i) => i.id === indicationId)
  );
  const updateIndication = useProjectStore((s) => s.updateIndication);
  const imgRef = useRef<HTMLImageElement>(null);

  if (!indication) return <p className="fp-popup__side-empty">Not found</p>;

  const typeConfig = INDICATION_TYPES[indication.type as keyof typeof INDICATION_TYPES];
  const typeColor = typeConfig?.color || '#888';
  // Imagery: a child fault (PFC-4B) has no image of its own — resolve the
  // SOURCE photo's thermal + visual/zoom pair via sourcePhotoId (display only;
  // never written to the child). The fault's OWN ROI (thermalData) overlays on
  // top. Mirrors IndicationPreview so the mini-map popup shows the same imagery.
  const ownImagePath = indication.properties.image || '';
  const sourcePhoto =
    !ownImagePath && indication.properties.sourcePhotoId
      ? useProjectStore.getState().data.indications.find((i) => i.id === indication.properties.sourcePhotoId)
      : undefined;
  const imageryProps = sourcePhoto?.properties ?? indication.properties;
  const imagePath = imageryProps.image || '';
  const imageSrc = imagePath ? resolveImageSrc(imagePath) : '';
  const visualImage = imageryProps.visualDriveId
    ? driveThumbnailUrl(imageryProps.visualDriveId)
    : (imageryProps.visualImage ? resolveImageSrc(imageryProps.visualImage) : undefined);
  const zoomImage = imageryProps.zoomDriveId
    ? driveThumbnailUrl(imageryProps.zoomDriveId)
    : (imageryProps.zoomImage ? resolveImageSrc(imageryProps.zoomImage) : undefined);
  const thermalData = indication.properties.thermalData;
  const hasThermalOverlay = thermalData &&
    ((thermalData.rois?.length ?? 0) > 0 || (thermalData.points?.length ?? 0) > 0);

  // Coordinates
  let coordStr = '';
  if (indication.geometry.type === 'Polygon') {
    const c = centroidOfPolygon(indication.geometry.coordinates[0] as [number, number][]);
    if (c) coordStr = `${c[0].toFixed(5)}, ${c[1].toFixed(5)}`;
  }

  const handleTypeChange = (newType: string) => {
    // String OC: same rebuild as the preview popup — the full string under
    // the marker's centre (grid count, rows, bearing), centre untouched.
    if (newType === 'String OC' && indication.geometry.type === 'Polygon') {
      const center = centroidOfPolygon(indication.geometry.coordinates[0] as [number, number][]);
      if (center) {
        const { geometry, rotationDeg } = retypedStringOcGeometry(
          center,
          indication.properties.rotationDeg || 0,
        );
        updateIndication(indicationId, {
          type: newType as IndicationType,
          geometry,
          properties: { title: newType, rotationDeg },
        });
        return;
      }
    }
    updateIndication(indicationId, {
      type: newType as any,
      properties: { title: newType },
    });
  };

  const handleFieldChange = (field: string, value: string) => {
    updateIndication(indicationId, { properties: { [field]: value } });
  };

  const handleNotesChange = (value: string) => {
    updateIndication(indicationId, { properties: { description: value } });
  };

  const handleDeleteClick = () => {
    if (window.confirm('Delete this indication? This cannot be undone.')) {
      onDelete(indicationId);
    }
  };

  const noop = () => {};
  const userNotes = indication.properties.description || '';

  return (
    <>
      {/* Header */}
      <div className="fp-popup__side-header">
        <span className="fp-popup__side-type-dot" style={{ background: typeColor }} />
        <select
          className="fp-popup__side-type-select"
          value={indication.type}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {Object.entries(INDICATION_TYPES).map(([type, config]) => (
            <option key={type} value={type}>{config.label}</option>
          ))}
        </select>
        {indication.type === 'Custom' && (
          <input
            className="fp-popup__side-custom-name"
            type="text"
            placeholder="Custom name"
            defaultValue={indication.properties.customTypeName || ''}
            onBlur={(e) => handleFieldChange('customTypeName', e.target.value.trim())}
          />
        )}
        {coordStr && <span className="fp-popup__side-coords">{coordStr}</span>}
        <button className="fp-popup__close-btn" onClick={onClose} title="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="fp-popup__side-content">
        {/* Photo — thermal/visual/zoom toggle, shared with IndicationPreview so
            the mini-map popup matches the main-map preview (incl. PFC-4B child
            faults resolving the source photo's visual/zoom pair). */}
        {imageSrc && (
          <PhotoSection
            imageSrc={imageSrc}
            thermalData={thermalData}
            hasThermalOverlay={hasThermalOverlay}
            thermalImgRef={imgRef}
            noop={noop}
            visualImage={visualImage}
            zoomImage={zoomImage}
          />
        )}

        {/* Inline-editable fields (matches IndicationPreview) */}
        <div className="indication-preview__fields">
          {SIDE_FIELDS.map((f) => (
            <label key={f.key} className="indication-preview__field-label">
              <span>{f.label}</span>
              <input
                type="text"
                className="indication-preview__field-input"
                value={indication.properties[f.key] || ''}
                placeholder="—"
                onChange={(e) => handleFieldChange(f.key, e.target.value)}
              />
            </label>
          ))}
        </div>

        {/* Notes box — ΔT label + editable textarea (matches IndicationPreview) */}
        <div className="indication-preview__notes-box">
          {thermalData?.rois?.some((r) => r.stats && r.stats.delta_t > 0) && (
            <span className="indication-preview__delta-tag">
              {thermalData.rois
                .filter((r) => r.stats && r.stats.delta_t > 0)
                .map((r, i) => `ΔT${thermalData.rois.length > 1 ? (i + 1) : ''} ${r.stats!.delta_t.toFixed(1)}°C`)
                .join(' · ')}
            </span>
          )}
          <textarea
            className="indication-preview__notes-input"
            value={userNotes}
            placeholder="Notes..."
            rows={2}
            onChange={(e) => handleNotesChange(e.target.value)}
          />
        </div>

        {/* Footer: checkbox + action buttons (matches IndicationPreview) */}
        <div className="indication-preview__actions">
          <label className="indication-preview__checkbox">
            <input
              type="checkbox"
              checked={!!indication.properties.createIssue}
              onChange={(e) => {
                const checked = e.target.checked;
                updateIndication(indicationId, { properties: { createIssue: checked } });
                if (indication) {
                  const siteId = useProjectStore.getState().meta.siteId ?? '';
                  useTaskStore.getState().syncOrRemoveIssueForIndication(indication, siteId, checked);
                }
              }}
            />
            <span>Issue</span>
          </label>
          <button className="indication-preview__btn indication-preview__btn--primary" onClick={() => onEdit(indicationId)}>
            Edit
          </button>
          <button className="indication-preview__btn indication-preview__btn--danger" onClick={handleDeleteClick}>
            Delete
          </button>
          <button className="indication-preview__btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}

// ─── PhotoSideView ───────────────────────────────────────────────────────────

function PhotoSideView({ photoId, onClose }: { photoId: string; onClose: () => void }) {
  const photo = useProjectStore((s) => s.data.geoPhotos.find((p) => p.id === photoId));
  if (!photo) return <p className="fp-popup__side-empty">Not found</p>;

  return (
    <>
      <div className="fp-popup__side-header">
        <h4 className="fp-popup__side-title">Photo</h4>
        <button className="fp-popup__close-btn" onClick={onClose} title="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="fp-popup__side-content">
        <img src={photo.url} alt="" className="fp-popup__side-photo" />
        <span className="fp-popup__side-coords">
          {photo.coordinates[1].toFixed(5)}, {photo.coordinates[0].toFixed(5)}
        </span>
      </div>
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

type PopupMode = 'player' | 'edit';
type VideoRole = 'thermal' | 'visual' | 'zoom' | 'screen';

/** Display order + labels for the four flight-video roles. `_S` is labeled
 *  "Screen (liveview)" to make clear it is the app's current-view recording,
 *  not an HD inspection camera feed. */
const ROLE_ORDER: VideoRole[] = ['thermal', 'visual', 'zoom', 'screen'];
const ROLE_LABELS: Record<VideoRole, string> = {
  thermal: 'Thermal',
  visual: 'Visual',
  zoom: 'Zoom',
  screen: 'Screen (liveview)',
};

/** First role with a non-empty URL, in ROLE_ORDER preference. */
function firstAvailableRole(urls: Record<VideoRole, string>): VideoRole {
  return ROLE_ORDER.find((r) => urls[r].trim()) ?? 'thermal';
}

export default function FlightPathPopup({ flightPathId, onClose }: FlightPathPopupProps) {
  const fp = useProjectStore.getState().data.flightPaths.find((f) => f.id === flightPathId);

  // Local state — one URL per role, plus per-role import-in-flight flags.
  const [urls, setUrls] = useState<Record<VideoRole, string>>(() => ({
    thermal: fp?.thermalVideoUrl || '',
    visual: fp?.visualVideoUrl || '',
    zoom: fp?.zoomVideoUrl || '',
    screen: fp?.screenVideoUrl || '',
  }));
  const [importing, setImporting] = useState<Record<VideoRole, boolean>>({
    thermal: false, visual: false, zoom: false, screen: false,
  });

  const availableRoles = ROLE_ORDER.filter((r) => urls[r].trim());
  const hasAnyUrl = availableRoles.length > 0;

  const [mode, setMode] = useState<PopupMode>(() => hasAnyUrl ? 'player' : 'edit');
  const [activeRole, setActiveRole] = useState<VideoRole>(() => firstAvailableRole(urls));

  const activeVideoUrl = urls[activeRole];
  const isIframeEmbed = !!getEmbedUrl(activeVideoUrl);

  // Minimap & frame tracking
  const videoRef = useRef<HTMLVideoElement>(null);
  const miniMapContainerRef = useRef<HTMLDivElement>(null);
  const miniMapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const [frameIndex, setFrameIndex] = useState(0);
  const mainMap = useMapStore((s) => s.map);

  // Codec fallback: a native <video> can fetch the file fine yet fail to decode
  // it (e.g. HEVC/H.265 from DJI, which Chrome/Edge on Windows can't play). Track
  // that so we replace the silent, dead player with a clear notice instead.
  const [videoUnplayable, setVideoUnplayable] = useState(false);
  useEffect(() => { setVideoUnplayable(false); }, [activeVideoUrl]);

  // Pre-compute for hooks (before early return)
  const coords = fp?.coordinates ?? [];
  const fpColor = fp?.color ?? '#3B82F6';

  const timeIndex = useMemo(
    () => (fp?.timestamps ? buildTimeIndex(fp.timestamps) : []),
    [fp?.timestamps],
  );

  // Side panel (Stage 1) and full edit overlay (Stage 2)
  const [selectedFeature, setSelectedFeature] = useState<{ type: 'indication' | 'photo'; id: string } | null>(null);
  const [editingIndicationId, setEditingIndicationId] = useState<string | null>(null);

  // Timer-based tracking for iframe embeds
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timerStartRef = useRef<number>(0);
  const timerOffsetRef = useRef<number>(0);
  const animFrameRef = useRef<number | null>(null);

  // ── Minimap ─────────────────────────────────────────────────────

  useEffect(() => {
    if (mode !== 'player' || !miniMapContainerRef.current || !mainMap || coords.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    coords.forEach((c) => bounds.extend([c[0], c[1]]));

    const miniMap = new mapboxgl.Map({
      container: miniMapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      bounds,
      // Frame the route at the MAIN map's rotation so the minimap reads the
      // same way as the editor behind the popup; kept in sync below.
      fitBoundsOptions: { padding: 30, bearing: mainMap.getBearing() },
      attributionControl: false,
      dragRotate: false,
    });
    miniMapRef.current = miniMap;

    miniMap.addControl(
      new mapboxgl.NavigationControl({ showCompass: false, visualizePitch: false }),
      'top-right',
    );

    const syncBearing = () => miniMap.setBearing(mainMap.getBearing());
    mainMap.on?.('rotate', syncBearing);

    let cancelled = false;

    miniMap.on('style.load', async () => {
      const orthos = useProjectStore.getState().data.orthophotos;
      const overlays = useProjectStore.getState().data.overlays;

      // Mapbox image sources load via Image() with crossOrigin='anonymous',
      // which strips cookies — raw /api/v2/assets/ URLs return 401. Pre-sign
      // the asset URLs (tile-mode items skip this; tiles aren't auth-gated).
      const urlsNeedingResolution = [
        ...orthos
          .filter((o) => o.visible && o.url && !(o.tileStatus === 'ready' && !!o.tilesetId))
          .map((o) => o.url),
        ...overlays
          .filter((o) => o.visible && o.url && !(o.tileStatus === 'ready' && !!o.tilesetId))
          .map((o) => o.url),
      ];
      const signedMap = urlsNeedingResolution.length
        ? await resolveSignedUrls(urlsNeedingResolution)
        : new Map<string, string>();

      if (cancelled || miniMapRef.current !== miniMap) return;

      // Copy orthophoto layers
      for (const ortho of orthos) {
        if (!ortho.visible || !ortho.url) continue;
        const srcId = `ortho-src-${ortho.id}`;
        if (miniMap.getSource(srcId)) continue;

        const useTiles = ortho.tileStatus === 'ready' && !!ortho.tilesetId;
        if (useTiles) {
          miniMap.addSource(srcId, {
            type: 'raster',
            tiles: [buildTileUrl(ortho.tilesetId!)],
            tileSize: 512,
            bounds: [ortho.bounds.minLng, ortho.bounds.minLat, ortho.bounds.maxLng, ortho.bounds.maxLat] as [number, number, number, number],
            minzoom: ortho.tileMinZoom ?? 10,
            maxzoom: ortho.tileMaxZoom ?? 22,
          });
        } else {
          miniMap.addSource(srcId, {
            type: 'image',
            url: signedMap.get(ortho.url) ?? ortho.url,
            coordinates: ortho.coordinates,
          });
        }
        miniMap.addLayer({
          id: `ortho-lyr-${ortho.id}`,
          type: 'raster',
          source: srcId,
          paint: { 'raster-opacity': ortho.opacity / 100, 'raster-fade-duration': 0 },
        });
      }

      // Copy overlay layers (PNG overlays)
      for (const ov of overlays) {
        if (!ov.visible || !ov.url) continue;
        const srcId = `overlay-src-${ov.id}`;
        if (miniMap.getSource(srcId)) continue;

        const useTiles = ov.tileStatus === 'ready' && !!ov.tilesetId;
        if (useTiles) {
          const lngs = ov.bounds.map((c) => c[0]);
          const lats = ov.bounds.map((c) => c[1]);
          miniMap.addSource(srcId, {
            type: 'raster',
            tiles: [buildTileUrl(ov.tilesetId!)],
            tileSize: 512,
            bounds: [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)] as [number, number, number, number],
            minzoom: ov.tileMinZoom ?? 10,
            maxzoom: ov.tileMaxZoom ?? 22,
          });
        } else {
          miniMap.addSource(srcId, {
            type: 'image',
            url: signedMap.get(ov.url) ?? ov.url,
            coordinates: ov.bounds,
          });
        }
        miniMap.addLayer({
          id: `overlay-lyr-${ov.id}`,
          type: 'raster',
          source: srcId,
          paint: { 'raster-opacity': ov.opacity, 'raster-fade-duration': 0 },
        });
      }

      // Draw indication polygons (colored fills + outlines)
      const indications = useProjectStore.getState().data.indications;
      const indicationFeatures = indications
        .filter((ind) => ind.geometry?.type === 'Polygon')
        .map((ind) => ({
          type: 'Feature' as const,
          geometry: ind.geometry,
          properties: {
            id: ind.id,
            type: ind.type,
            color: INDICATION_TYPES[ind.type as keyof typeof INDICATION_TYPES]?.color || '#FF3B30',
          },
        }));
      if (indicationFeatures.length > 0) {
        miniMap.addSource('indications', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: indicationFeatures },
        });
        miniMap.addLayer({
          id: 'indications-fill',
          type: 'fill',
          source: 'indications',
          paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.2 },
        });
        miniMap.addLayer({
          id: 'indications-outline',
          type: 'line',
          source: 'indications',
          paint: { 'line-color': ['get', 'color'], 'line-width': 1.5 },
        });
      }

      // Draw geo-photo markers (blue circles)
      const geoPhotos = useProjectStore.getState().data.geoPhotos;
      if (geoPhotos.length > 0) {
        miniMap.addSource('geo-photos', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: geoPhotos.map((p) => ({
              type: 'Feature' as const,
              geometry: { type: 'Point' as const, coordinates: p.coordinates },
              properties: { id: p.id },
            })),
          },
        });
        miniMap.addLayer({
          id: 'geo-photos-point',
          type: 'circle',
          source: 'geo-photos',
          paint: {
            'circle-radius': 4,
            'circle-color': '#3b82f6',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
          },
        });
      }

      // Draw flight path line (on top of all other layers)
      miniMap.addSource('fp-line', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords.map((c) => [c[0], c[1]]) },
          properties: {},
        },
      });
      miniMap.addLayer({
        id: 'fp-line-layer',
        type: 'line',
        source: 'fp-line',
        paint: { 'line-color': fpColor, 'line-width': 3 },
      });

      // ── Clickable indications & geo-photos ──
      miniMap.on('click', 'indications-fill', (e) => {
        if (!e.features?.[0]?.properties?.id) return;
        setSelectedFeature({ type: 'indication', id: e.features[0].properties.id as string });
      });
      miniMap.on('click', 'geo-photos-point', (e) => {
        if (!e.features?.[0]?.properties?.id) return;
        setSelectedFeature({ type: 'photo', id: e.features[0].properties.id as string });
      });
      miniMap.on('mouseenter', 'indications-fill', () => { miniMap.getCanvas().style.cursor = 'pointer'; });
      miniMap.on('mouseleave', 'indications-fill', () => { miniMap.getCanvas().style.cursor = ''; });
      miniMap.on('mouseenter', 'geo-photos-point', () => { miniMap.getCanvas().style.cursor = 'pointer'; });
      miniMap.on('mouseleave', 'geo-photos-point', () => { miniMap.getCanvas().style.cursor = ''; });
    });

    // Position marker at first frame
    const marker = new mapboxgl.Marker({ color: '#FF3B30' })
      .setLngLat([coords[0][0], coords[0][1]])
      .addTo(miniMap);
    markerRef.current = marker;

    return () => {
      cancelled = true;
      mainMap.off?.('rotate', syncBearing);
      miniMap.remove();
      miniMapRef.current = null;
      markerRef.current = null;
    };
  }, [mode, mainMap, coords, fpColor]);

  // Update marker when frameIndex changes
  useEffect(() => {
    if (!markerRef.current || !coords[frameIndex]) return;
    const [lng, lat] = coords[frameIndex];
    markerRef.current.setLngLat([lng, lat]);
    miniMapRef.current?.panTo([lng, lat], { duration: 200 });
  }, [frameIndex, coords]);

  // Auto-sync scrubber with <video> playback
  useEffect(() => {
    if (mode !== 'player' || isIframeEmbed) return;
    const video = videoRef.current;
    if (!video || timeIndex.length === 0) return;

    const onTimeUpdate = () => {
      const idx = findFrameAtTime(timeIndex, video.currentTime);
      setFrameIndex(idx);
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [timeIndex, activeVideoUrl, mode, isIframeEmbed]);

  // Timer-based auto-sync for iframe embeds
  useEffect(() => {
    if (!isTimerRunning || timeIndex.length === 0) return;

    const tick = () => {
      const elapsedSec = timerOffsetRef.current + (performance.now() - timerStartRef.current) / 1000;
      const totalDuration = timeIndex[timeIndex.length - 1] - timeIndex[0];

      if (elapsedSec >= totalDuration) {
        setFrameIndex(timeIndex.length - 1);
        setIsTimerRunning(false);
        return;
      }

      const absoluteTime = timeIndex[0] + elapsedSec;
      const idx = findFrameAtTime(timeIndex, absoluteTime);
      setFrameIndex(idx);
      animFrameRef.current = requestAnimationFrame(tick);
    };

    timerStartRef.current = performance.now();
    animFrameRef.current = requestAnimationFrame(tick);

    return () => {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  }, [isTimerRunning, timeIndex]);

  // Reset timer when video source or mode changes
  useEffect(() => {
    setIsTimerRunning(false);
    timerOffsetRef.current = 0;
    timerStartRef.current = 0;
  }, [activeVideoUrl, mode]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handleSave = useCallback(() => {
    useProjectStore.getState().updateFlightPath(flightPathId, {
      thermalVideoUrl: urls.thermal.trim() || undefined,
      visualVideoUrl: urls.visual.trim() || undefined,
      zoomVideoUrl: urls.zoom.trim() || undefined,
      screenVideoUrl: urls.screen.trim() || undefined,
    });
    // Switch to player if any URL now exists
    if (ROLE_ORDER.some((r) => urls[r].trim())) {
      setActiveRole(firstAvailableRole(urls));
      setMode('player');
    }
  }, [flightPathId, urls]);

  const handleDelete = useCallback(() => {
    if (!confirm('Delete this flight path?')) return;
    useProjectStore.getState().removeFlightPath(flightPathId);
    onClose();
  }, [flightPathId, onClose]);

  const handleOpenUrl = useCallback((url: string) => {
    const trimmed = url.trim();
    if (!trimmed) {
      alert('No URL set.');
      return;
    }
    window.open(trimmed, '_blank', 'noopener');
  }, []);

  const switchToEdit = useCallback(() => { setSelectedFeature(null); setMode('edit'); }, []);
  const switchToPlayer = useCallback(() => {
    setActiveRole(firstAvailableRole(urls));
    setMode('player');
  }, [urls]);

  const handleTimerPlay = useCallback(() => {
    setIsTimerRunning(true);
  }, []);

  const handleTimerPause = useCallback(() => {
    if (isTimerRunning) {
      timerOffsetRef.current += (performance.now() - timerStartRef.current) / 1000;
    }
    setIsTimerRunning(false);
  }, [isTimerRunning]);

  const handleTimerReset = useCallback(() => {
    setIsTimerRunning(false);
    timerOffsetRef.current = 0;
    timerStartRef.current = 0;
    setFrameIndex(0);
  }, []);

  const handleImportToCloud = useCallback(async (role: VideoRole, url: string) => {
    const siteId = useProjectStore.getState().meta.siteId;
    if (!siteId) {
      useUIStore.getState().addNotification({ type: 'error', title: 'No project selected', message: 'Save the project first to enable cloud video hosting.' });
      return;
    }
    setImporting((prev) => ({ ...prev, [role]: true }));
    try {
      const result = await importDriveVideo(siteId, url);
      setUrls((prev) => ({ ...prev, [role]: result.url }));
      useUIStore.getState().addNotification({ type: 'success', title: 'Video uploaded to cloud', message: `${(result.size / (1024 * 1024)).toFixed(1)} MB uploaded. Click Save to keep.` });
    } catch (err) {
      useUIStore.getState().addNotification({ type: 'error', title: 'Video import failed', message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setImporting((prev) => ({ ...prev, [role]: false }));
    }
  }, []);

  // ── Stage 2: Full edit handlers ──────────────────────────────────

  const getIndicationCoordinates = useCallback((id: string): [number, number] => {
    const ind = useProjectStore.getState().data.indications.find((i) => i.id === id);
    if (!ind) return [0, 0];
    if (ind.geometry.type === 'Point') return ind.geometry.coordinates as [number, number];
    if (ind.geometry.type === 'Polygon')
      return centroidOfPolygon(ind.geometry.coordinates[0] as [number, number][]) || [0, 0];
    return [0, 0];
  }, []);

  const handleFullEditSave = useCallback((id: string, formData: IndicationFormData) => {
    // Same save contract as EditorPage.handleEditIndicationSave — this path
    // used to silently DROP mppt and customFields (a banner-staged mppt or
    // custom field vanished when editing through the flight-path popup),
    // and it now stamps human provenance on genuinely changed fields.
    const prevInd = useProjectStore.getState().data.indications.find((i) => i.id === id);
    const editedTopoFields = prevInd
      ? (['station', 'inverter', 'mppt', 'dccb', 'string', 'module'] as const).filter((f) => {
          const before = (prevInd.properties[f] ?? '').trim();
          const after = (formData[f] ?? '').trim();
          if (before === after) return false;
          return before === '' || after === '' || !topologyValuesEqual(before, after);
        })
      : [];
    useProjectStore.getState().updateIndication(id, {
      type: formData.type as any,
      properties: {
        title: formData.type,
        description: formData.notes,
        captureDate: formData.captureDate,
        station: formData.station,
        inverter: formData.inverter,
        mppt: formData.mppt,
        dccb: formData.dccb,
        string: formData.string,
        module: formData.module,
        customFields: formData.customFields,
        linkLibrary: formData.linkLibrary,
        urlOverride: formData.urlOverride,
        srtUrl: formData.srtUrl,
        createIssue: formData.createIssue,
        thermalData: formData.thermalData,
        ...(editedTopoFields.length > 0 && prevInd
          ? humanEditProvenance(
              prevInd.properties as unknown as Record<string, unknown>,
              editedTopoFields as unknown as string[],
            )
          : {}),
      },
    });
    setEditingIndicationId(null);
  }, []);

  const handleFullEditDelete = useCallback((id: string) => {
    useProjectStore.getState().removeIndication(id);
    setEditingIndicationId(null);
    setSelectedFeature(null);
  }, []);

  const handleSideDelete = useCallback((id: string) => {
    useProjectStore.getState().removeIndication(id);
    setSelectedFeature(null);
  }, []);

  // Bail if flight path not found
  if (!fp) return null;

  const stats = computeStats(fp);
  const startCoord = coords[0];
  const endCoord = coords[coords.length - 1];

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="fp-popup-backdrop" onClick={onClose}>
      <div className="fp-popup-wrapper" onClick={(e) => e.stopPropagation()}>
      <div className="fp-popup">
        {/* Header */}
        <div className="fp-popup__header">
          <h3>{mode === 'player' ? 'Flight Video' : 'Flight Path'}</h3>
          <span className="fp-popup__flight-name" title={fp.name}>{fp.name}</span>
          <button className="fp-popup__close-btn" onClick={onClose} title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="fp-popup__content">
          {mode === 'player' ? (
            <>
              {/* Video player */}
              <div className="fp-popup__video-wrap">
                {getEmbedUrl(activeVideoUrl) ? (
                  <iframe
                    key={activeVideoUrl}
                    className="fp-popup__iframe"
                    src={getEmbedUrl(activeVideoUrl)!}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />
                ) : (
                  <video
                    ref={videoRef}
                    key={activeVideoUrl}
                    className="fp-popup__video"
                    controls={!videoUnplayable}
                    autoPlay
                    src={toVideoSrc(activeVideoUrl)}
                    onError={() => {
                      // 3 = MEDIA_ERR_DECODE, 4 = MEDIA_ERR_SRC_NOT_SUPPORTED:
                      // the bytes arrived but the browser can't decode the codec.
                      const code = videoRef.current?.error?.code;
                      if (code === 3 || code === 4) setVideoUnplayable(true);
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
                {videoUnplayable && !getEmbedUrl(activeVideoUrl) && (
                  <div className="fp-popup__video-error">
                    <div className="fp-popup__video-error-title">Can’t play this video here</div>
                    <p className="fp-popup__video-error-msg">
                      Your browser can’t decode this file’s format (often HEVC/H.265 from DJI).
                      The route is still usable — scrub it manually below.
                    </p>
                    <button
                      type="button"
                      className="fp-popup__video-error-btn"
                      onClick={async () => {
                        // Signed URL serves without a cookie, so the download
                        // works cross-origin in dev and prod alike.
                        const signed = await resolveSignedUrls([activeVideoUrl]);
                        const target = signed.get(activeVideoUrl) ?? activeVideoUrl;
                        // Only open http(s)/same-origin URLs — never a
                        // javascript:/data: scheme that could ride in via a
                        // shared project's video-URL field.
                        let safe = false;
                        try {
                          const proto = new URL(target, window.location.origin).protocol;
                          safe = proto === 'http:' || proto === 'https:';
                        } catch { /* malformed URL → not safe */ }
                        if (safe) window.open(target, '_blank', 'noopener');
                      }}
                    >
                      Download file
                    </button>
                  </div>
                )}
              </div>

              {/* Dynamic role chips — one per available role (thermal/visual/zoom/screen) */}
              <div className="fp-popup__source-toggle">
                {availableRoles.map((role) => (
                  <button
                    key={role}
                    className={`fp-popup__source-btn${activeRole === role ? ' fp-popup__source-btn--active' : ''}`}
                    onClick={() => setActiveRole(role)}
                  >
                    {ROLE_LABELS[role]}
                  </button>
                ))}
              </div>

              {/* Minimap + Frame Scrubber */}
              {coords.length > 1 && (
                <>
                  <div ref={miniMapContainerRef} className="fp-popup__minimap" />

                  {/* Timer controls for iframe embeds */}
                  {isIframeEmbed && timeIndex.length > 0 && (
                    <div className="fp-popup__timer-controls">
                      <button
                        className={`fp-popup__timer-btn ${isTimerRunning ? 'fp-popup__timer-btn--active' : ''}`}
                        onClick={isTimerRunning ? handleTimerPause : handleTimerPlay}
                        title={isTimerRunning ? 'Pause tracking' : 'Start tracking'}
                      >
                        {isTimerRunning ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16" />
                            <rect x="14" y="4" width="4" height="16" />
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5,3 19,12 5,21" />
                          </svg>
                        )}
                        <span>{isTimerRunning ? 'Tracking' : 'Start Tracking'}</span>
                      </button>
                      <button
                        className="fp-popup__timer-btn fp-popup__timer-btn--reset"
                        onClick={handleTimerReset}
                        title="Reset to start"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="1,4 1,10 7,10" />
                          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                        </svg>
                      </button>
                    </div>
                  )}

                  <div className="fp-popup__scrubber">
                    <input
                      type="range"
                      min={0}
                      max={coords.length - 1}
                      value={frameIndex}
                      onChange={(e) => {
                        const newIndex = parseInt(e.target.value);
                        setFrameIndex(newIndex);
                        if (isIframeEmbed) {
                          if (isTimerRunning) setIsTimerRunning(false);
                          timerOffsetRef.current = timeIndex.length > 0
                            ? timeIndex[newIndex] - timeIndex[0]
                            : 0;
                        }
                      }}
                      className="fp-popup__scrubber-input"
                    />
                    <div className="fp-popup__frame-info">
                      <span>Frame {frameIndex + 1}/{coords.length}</span>
                      {isIframeEmbed && timeIndex.length > 0 && (
                        <span>
                          {formatElapsed(timeIndex[frameIndex] - timeIndex[0])}
                          {' / '}
                          {formatElapsed(timeIndex[timeIndex.length - 1] - timeIndex[0])}
                        </span>
                      )}
                      <span>{fmtGps(coords[frameIndex])}</span>
                    </div>
                  </div>
                </>
              )}

              {/* Compact stats */}
              <div className="fp-popup__stats">
                {stats.durationSec != null && stats.durationSec > 0 && (
                  <div><b>Duration:</b> {stats.durationSec.toFixed(1)}s{stats.frameCount ? ` (${stats.frameCount} frames)` : ''}</div>
                )}
                {startCoord && (
                  <div><b>Start GPS:</b> {fp.startGps || fmtGps(startCoord)}</div>
                )}
                {stats.avgAltitude != null && (
                  <div><b>Avg altitude:</b> {stats.avgAltitude.toFixed(1)}m</div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Edit: Full stats */}
              <div className="fp-popup__stats">
                {stats.durationSec != null && stats.durationSec > 0 && (
                  <div><b>Duration:</b> {stats.durationSec.toFixed(1)}s{stats.frameCount ? ` (${stats.frameCount} frames)` : ''}</div>
                )}
                {fp.startTime && (
                  <div><b>Start:</b> {fp.startTime}</div>
                )}
                {startCoord && (
                  <div><b>Start GPS:</b> {fp.startGps || fmtGps(startCoord)}</div>
                )}
                {endCoord && (
                  <div><b>End GPS:</b> {fp.endGps || fmtGps(endCoord)}</div>
                )}
                {stats.avgAltitude != null && (
                  <div><b>Avg altitude:</b> {stats.avgAltitude.toFixed(1)}m</div>
                )}
              </div>

              <div className="fp-popup__divider" />

              {/* One URL row per role: Thermal / Visual / Zoom / Screen (liveview) */}
              {ROLE_ORDER.map((role) => {
                const url = urls[role];
                const has = !!url.trim();
                const isImporting = importing[role];
                return (
                  <div className="fp-popup__form-group" key={role}>
                    <label>{ROLE_LABELS[role]} Video URL</label>
                    <div className="fp-popup__url-row">
                      <input
                        className="fp-popup__url-input"
                        type="url"
                        value={url}
                        onChange={(e) => setUrls((prev) => ({ ...prev, [role]: e.target.value }))}
                        placeholder="https://..."
                        disabled={isImporting}
                      />
                      {has && (
                        <button
                          className="fp-popup__open-link"
                          onClick={() => handleOpenUrl(url)}
                          title="Open in new tab"
                        >
                          Open
                        </button>
                      )}
                      {has && detectVideoType(url) === 'google-drive' && (
                        <button
                          className="fp-popup__cloud-upload-btn"
                          onClick={() => handleImportToCloud(role, url)}
                          disabled={isImporting}
                          title="Upload to cloud for direct video playback with precise tracking"
                        >
                          {isImporting ? (
                            <span className="fp-popup__spinner" />
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 15V3m0 0l-4 4m4-4l4 4" />
                              <path d="M2 17l.621 2.485A2 2 0 004.561 21h14.878a2 2 0 001.94-1.515L22 17" />
                            </svg>
                          )}
                          <span>{isImporting ? 'Uploading...' : 'Cloud'}</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="fp-popup__footer">
          {mode === 'player' ? (
            <>
              <Button variant="ghost" size="sm" onClick={switchToEdit}>Edit</Button>
              <Button variant="danger" size="sm" onClick={handleDelete}>Delete</Button>
              <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
            </>
          ) : (
            <>
              <Button variant="primary" size="sm" onClick={handleSave}>Save</Button>
              {hasAnyUrl && (
                <Button variant="ghost" size="sm" onClick={switchToPlayer}>Player</Button>
              )}
              <Button variant="danger" size="sm" onClick={handleDelete}>Delete</Button>
              <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
            </>
          )}
        </div>
      </div>

      {/* ══ Stage 1: Side Panel ══ */}
      {selectedFeature && mode === 'player' && (
        <div className="fp-popup__side-panel">
          {selectedFeature.type === 'indication' ? (
            <IndicationSideView
              indicationId={selectedFeature.id}
              onEdit={(id) => setEditingIndicationId(id)}
              onDelete={handleSideDelete}
              onClose={() => setSelectedFeature(null)}
            />
          ) : (
            <PhotoSideView
              photoId={selectedFeature.id}
              onClose={() => setSelectedFeature(null)}
            />
          )}
        </div>
      )}
      </div>

      {/* ══ Stage 2: Full Edit Overlay ══ */}
      {editingIndicationId && (
        <div className="fp-popup__edit-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="fp-popup__edit-overlay-backdrop" onClick={() => setEditingIndicationId(null)} />
          <IndicationPopup
            mode="edit"
            coordinates={getIndicationCoordinates(editingIndicationId)}
            indicationId={editingIndicationId}
            onSave={(formData) => handleFullEditSave(editingIndicationId, formData)}
            onCancel={() => setEditingIndicationId(null)}
            onDelete={() => handleFullEditDelete(editingIndicationId)}
          />
        </div>
      )}
    </div>
  );
}
